import { Component, EventEmitter, Input, Output } from '@angular/core';

import { take } from 'rxjs';
import { MessageService } from 'primeng/api';

import { MentionsApi } from '../../../../api/mentions/mentions.api';

import { LocalizationService } from '../../../../Core/Localization/services/localization.service';

import { BulkMentionRequestModel } from '../../../../Monitor/Shared/models/bulk-mention-request-model';
import { MonitoringBulkActionSourceMethodEnum } from '../../../../Monitor/Shared/enums/monitoring-bulk-action-source-method.enum';
import { BulkMentionActionModel } from '../../../../Monitor/Shared/models/bulk-mention-action.model';
import { MonitoringFileTypesEnum } from '../../../../Monitor/Shared/enums/monitoring-file-types.enum';

import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { Mention } from '../../models/mention';

enum TranslateTabIds {
    ORIGINAL,
    TRANSLATED
}

@Component({
    selector: 'agpr-mention-set-preferred',
    templateUrl: './mention-set-preferred.component.html',
    styleUrls: ['./mention-set-preferred.component.scss'],
    standalone: false
})
export class MentionSetPreferredComponent {
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;

    @Input() id: string;
    @Input() selectedTabIndex: number = 0;
    @Input() mention: Mention;
    @Input() openAiPermission: boolean = false;
    @Input() autoSave: boolean = false;
    @Input() isEditMention: boolean = false;
    @Input() isLanguagePanelHalfWidth: boolean = false;

    @Output() tabChanged: EventEmitter<number> = new EventEmitter<number>();
    @Output() preferredChanged: EventEmitter<void> = new EventEmitter<void>();
    @Output() translationRemoved: EventEmitter<void> = new EventEmitter<void>();

    tabIds = TranslateTabIds;

    constructor(
        public mentionsApi: MentionsApi,
        private messageService: MessageService,
        private localizationService: LocalizationService,
    ) {}

    changeTab(event) {
        this.tabChanged.emit(event);
    }

    removeTranslated() {
        if (this.mention.mentionId) {
            const modifications = new BulkMentionActionModel({
                removeFile: MonitoringFileTypesEnum.TRANSLATION
            });
            const bulkMentionRequest = new BulkMentionRequestModel(
                MonitoringBulkActionSourceMethodEnum.SELECTION,
                modifications,
                null,
                [this.mention.mentionId]
            );

            this.mention.translating = true;

            if (this.mention.isTranslatedPreferred) {
                this.setTranslatedPreferred();
            }

            this.mentionsApi.updateMentionsInBulk(bulkMentionRequest.toApi()).pipe(take(1)).subscribe({
                next: () => {
                    this.mention.translating = false;
                    this.selectedTabIndex = 0;
                    this.tabChanged.emit(this.selectedTabIndex);
                    delete this.mention.translation;
                    this.translationRemoved.emit();
                },
                error: (error) => {
                    this.mention.translating = false;
                    this.messageService.add(
                        {
                            severity: messageSeverityEnum.error,
                            summary: this.localizationService.transform('Mention.translate.removingTranslation.error.title'),
                            detail: this.localizationService.transform('Mention.translate.removingTranslation.error.description')
                        }
                    );
                }
            });
        }
    }

    setTranslatedPreferred() {
        this.mention.isTranslatedPreferred = !this.mention.isTranslatedPreferred;
        if (this.autoSave) {
            this.mention.translating = true;
            this.mention.showTranslated = false;
            this.mentionsApi.patchMention(this.mention.toUpdateMentionTranslationApiModel()).subscribe({
                next: () => {
                    this.mention.translating = false;
                },
                error: () => {
                    this.messageService.add(
                        {
                            severity: messageSeverityEnum.error,
                            summary: this.localizationService.transform('Mention.translate.setPreferred.error.title'),
                            detail: this.localizationService.transform('Mention.translate.setPreferred.error.description')
                        }
                    );
                    this.mention.translating = false;
                    this.mention.isTranslatedPreferred = !this.mention.isTranslatedPreferred;
                }
            });
        } else {
            this.preferredChanged.emit();
        }
    }
}
